import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
    Box, Typography, Paper, Button, Table, TableHead, TableRow, TableCell,
    TableBody, TableContainer, TablePagination, Chip, IconButton, TextField,
    FormControl, InputLabel, Select, MenuItem, Grid, Tabs, Tab, Divider,
    Snackbar, Alert, Skeleton, Dialog, DialogTitle, DialogContent, DialogActions,
    CircularProgress, Checkbox, ListItemText, OutlinedInput
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import DeleteConfirmationModal from '../../components/DeleteConfirmationModal';
import CreateCancellationPolicyModal from '../../components/CreateCancellationPolicyModal';
import CancellationRuleModal from '../../components/CancellationRuleModal';
import { SettingsService } from '../../api/services/settingsService';
import { extractTotalCount } from '../../components/common/tableStyles';

function TabPanel({ children, value, index, ...other }) {
    return (
        <div role="tabpanel" hidden={value !== index} {...other}>
            {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
        </div>
    );
}

const TIME_OPTIONS = ['More than 7 days', '3 - 7 days', '24 - 72 hours', 'Less than 24 hours', 'No Show'];
const FEE_OPTIONS = ['0% (Free Cancellation)', '25% of Booking Amount', '50% of Booking Amount', '75% of Booking Amount', '100% of Booking Amount'];
const APPLIED_TO_OPTIONS = ['Direct', 'OTA', 'Corporate', 'Group', 'Walk-in'];


// Helper: format fee display from API feeType + feeAmount
const formatFeeDisplay = (rule) => {
    if (!rule) return '';
    const amount = parseFloat(rule.feeAmount || 0);
    if (rule.feeType === 'Percentage') {
        if (amount === 0) return '0% (Free Cancellation)';
        return `${amount}% of Booking Amount`;
    }
    return `${amount} Fixed`;
};

// Helper: parse fee display string back to feeType + feeAmount for API
const parseFeeDisplay = (feeString) => {
    if (!feeString) return { feeType: 'Percentage', feeAmount: '0.00' };
    if (feeString.includes('Free Cancellation')) return { feeType: 'Percentage', feeAmount: '0.00' };
    const match = feeString.match(/(\d+)%/);
    if (match) return { feeType: 'Percentage', feeAmount: parseFloat(match[1]).toFixed(2) };
    return { feeType: 'Fixed', feeAmount: '0.00' };
};

export default function CancellationPoliciesPage() {
    const [policies, setPolicies] = useState([]);
    const [totalPolicies, setTotalPolicies] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [selectedPolicy, setSelectedPolicy] = useState(null);
    const [detailTab, setDetailTab] = useState(0);
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
    const { user } = useSelector((state) => state.auth || {});
    const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
    const tenantId = user?.tenantId || authData.tenantId || '1';

    const [refundMethodOptions, setRefundMethodOptions] = useState([]);
    const [loadingRefundMethods, setLoadingRefundMethods] = useState(false);

    // Form state (for editing existing policy in detail panel)
    const [formName, setFormName] = useState('');
    const [formStatus, setFormStatus] = useState('Active');
    const [formDescription, setFormDescription] = useState('');
    const [formRefundMethod, setFormRefundMethod] = useState('');
    const [formRules, setFormRules] = useState([]);
    const [isSavingRules, setIsSavingRules] = useState(false);

    // Filter available time options
    const selectedTimeOptions = formRules.map(r => r.timeBeforeCheckIn);
    const getAvailableTimeOptions = (currentRuleTime = null) => {
        return TIME_OPTIONS.filter(t => !selectedTimeOptions.includes(t) || t === currentRuleTime);
    };

    // Create modal state
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [isCreating, setIsCreating] = useState(false);

    // Delete state
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [policyToDelete, setPolicyToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Rule modal state
    const [ruleModalOpen, setRuleModalOpen] = useState(false);
    const [editingRule, setEditingRule] = useState(null);

    // Delete rule state
    const [deleteRuleModalOpen, setDeleteRuleModalOpen] = useState(false);
    const [ruleToDelete, setRuleToDelete] = useState(null);
    const [isDeletingRule, setIsDeletingRule] = useState(false);

    // Inline Rule Edit state
    const [editingRuleId, setEditingRuleId] = useState(null);
    const [inlineRuleData, setInlineRuleData] = useState({ timeBeforeCheckIn: '', feeType: '', feeAmount: '' });
    const [isUpdatingRow, setIsUpdatingRow] = useState(false);

    // Server-side pagination is now used.

    useEffect(() => {
        fetchPaymentMethods();
    }, [tenantId]);

    const fetchPaymentMethods = async () => {
        setLoadingRefundMethods(true);
        try {
            const res = await SettingsService.getPaymentMethods(tenantId, { noPagination: true });
            const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
            const activeList = list.filter(m => m.enabled === undefined || m.enabled === true || m.status === 'Active');
            const methodNames = activeList.map(m => m.name || m.label || (typeof m === 'string' ? m : '')).filter(Boolean);
            setRefundMethodOptions(methodNames);
        } catch (error) {
            console.error('Failed to load payment methods for cancellation policies:', error);
            setRefundMethodOptions([]);
        } finally {
            setLoadingRefundMethods(false);
        }
    };

    useEffect(() => {
        fetchCancellationPolicies();
    }, [page, rowsPerPage]);

    const fetchCancellationPolicies = async () => {
        setIsLoading(true);
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            const data = await SettingsService.getCancellationPolicies(tenantId, {
                page: page,
                limit: rowsPerPage
            });
            const policiesArray = Array.isArray(data.data) ? data.data : (Array.isArray(data) ? data : []);
            setPolicies(policiesArray);
            setTotalPolicies(extractTotalCount(data, policiesArray));

            setSelectedPolicy(prev => {
                if (prev) {
                    const updated = policiesArray.find(p => p.id === prev.id);
                    if (updated) {
                        setFormRules([...(updated.rules || [])]);
                        return updated;
                    }
                }
                return prev;
            });
        } catch (error) {
            console.error('Failed to fetch cancellation policies:', error);
            setNotification({ open: true, message: 'Failed to load cancellation policies.', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleSelectPolicy = (policy) => {
        if (selectedPolicy?.id === policy.id) {
            setSelectedPolicy(null);
            return;
        }
        setSelectedPolicy(policy);
        setFormName(policy.name);
        setFormStatus(policy.status);
        setFormDescription(policy.description || '');
        setFormRefundMethod(policy.refundMethod || '');
        setFormRules([...(policy.rules || [])]);
        setDetailTab(0);
    };

    // ─── Create Policy Modal Handlers ──────────────────────────────────────────
    const handleOpenCreateModal = () => {
        setCreateModalOpen(true);
    };

    const handleCreatePolicy = async (payload) => {
        setIsCreating(true);
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';

            await SettingsService.createCancellationPolicy(payload, tenantId);

            // Success: reset form, close modal, re-fetch grid
            setCreateModalOpen(false);
            setNotification({ open: true, message: 'Cancellation policy successfully created and registered.', severity: 'success' });
            fetchCancellationPolicies();
        } catch (error) {
            // Preserve form data on error — user can retry without re-typing
            const errMsg = error?.response?.data?.message || error?.message || 'Failed to create cancellation policy.';
            setNotification({ open: true, message: errMsg, severity: 'error' });
        } finally {
            setIsCreating(false);
        }
    };

    const handleSaveChanges = async () => {
        if (!selectedPolicy) return;

        if (!formName.trim()) {
            setNotification({ open: true, message: 'Policy name is required.', severity: 'error' });
            return;
        }

        setIsSavingRules(true);
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';

            let detailsUpdated = false;
            // 1. Update Policy Details if changed
            if (formName.trim() !== selectedPolicy.name || formStatus !== selectedPolicy.status) {
                const payload = { name: formName.trim(), status: formStatus };
                await SettingsService.updateCancellationPolicy(selectedPolicy.id, payload, tenantId);
                detailsUpdated = true;
            }

            // 2. Add any new draft rules by passing the full matrix
            const hasNewDraftRules = formRules.some(r => r.id.toString().startsWith('r'));
            if (hasNewDraftRules) {
                const rulesPayload = formRules.map(r => ({
                    timeBeforeCheckIn: r.timeBeforeCheckIn,
                    feeType: r.feeType,
                    feeAmount: parseFloat(r.feeAmount) || 0
                }));
                
                await SettingsService.updateCancellationPolicyRules(selectedPolicy.id, rulesPayload, tenantId);
                detailsUpdated = true;
            }

            if (detailsUpdated) {
                setNotification({ open: true, message: 'Cancellation policy successfully updated', severity: 'success' });
                fetchCancellationPolicies();
            } else {
                setNotification({ open: true, message: 'No changes to save.', severity: 'info' });
            }
        } catch (error) {
            const errMsg = error?.response?.data?.message || error?.message || 'Failed to update policy.';
            setNotification({ open: true, message: errMsg, severity: 'error' });
        } finally {
            setIsSavingRules(false);
        }
    };

    const handleDeleteClick = (policy) => {
        setPolicyToDelete(policy);
        setDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!policyToDelete) return;
        setIsDeleting(true);
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';

            await SettingsService.deleteCancellationPolicy(policyToDelete.id, tenantId);

            setNotification({ open: true, message: 'Cancellation policy permanently deleted.', severity: 'success' });
            if (selectedPolicy?.id === policyToDelete.id) setSelectedPolicy(null);
            setDeleteModalOpen(false);
            setPolicyToDelete(null);
            fetchCancellationPolicies();
        } catch (error) {
            const errMsg = error?.response?.data?.message || error?.message || 'Failed to delete cancellation policy.';
            setNotification({ open: true, message: errMsg, severity: 'error' });
        } finally {
            setIsDeleting(false);
        }
    };

    const handleOpenRuleModal = (rule = null) => {
        setEditingRule(rule);
        setRuleModalOpen(true);
    };

    const handleSaveRuleSubmit = async (timeBeforeCheckIn, ruleFeeString) => {
        if (!selectedPolicy) return;
        const { feeType, feeAmount } = parseFeeDisplay(ruleFeeString);
        
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';

            if (editingRule && editingRule.id && !editingRule.id.toString().startsWith('r')) {
                // Update existing rule via PATCH
                await SettingsService.updateCancellationRule(editingRule.id, { timeBeforeCheckIn, feeType, feeAmount }, tenantId);
                setNotification({ open: true, message: 'Rule updated successfully', severity: 'success' });
                fetchCancellationPolicies();
            } else if (editingRule && editingRule.id.toString().startsWith('r')) {
                // Update draft rule locally
                setFormRules(prev => prev.map(r => r.id === editingRule.id ? { ...r, timeBeforeCheckIn, feeType, feeAmount } : r));
                setNotification({ open: true, message: 'Draft rule updated successfully', severity: 'success' });
            } else {
                // Create new rule locally as draft
                setFormRules(prev => [...prev, { id: 'r' + Date.now(), timeBeforeCheckIn, feeType, feeAmount }]);
                setNotification({ open: true, message: 'Draft rule added. Click Save Changes to apply.', severity: 'info' });
            }
        } catch (error) {
            const errMsg = error?.response?.data?.message || error?.message || 'Failed to save rule.';
            setNotification({ open: true, message: errMsg, severity: 'error' });
        } finally {
            setRuleModalOpen(false);
        }
    };

    const handleStartInlineEdit = (rule) => {
        setEditingRuleId(rule.id);
        setInlineRuleData({
            timeBeforeCheckIn: rule.timeBeforeCheckIn,
            feeType: rule.feeType,
            feeAmount: rule.feeAmount || 0
        });
    };

    const handleSaveInlineRule = async (id) => {
        setIsUpdatingRow(true);
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';

            const payload = {
                timeBeforeCheckIn: inlineRuleData.timeBeforeCheckIn,
                feeType: inlineRuleData.feeType,
                feeAmount: parseFloat(inlineRuleData.feeAmount) || 0
            };

            // If it's a new draft rule, update locally only
            if (id && id.toString().startsWith('r')) {
                setFormRules(prev => prev.map(r => r.id === id ? { ...r, ...payload } : r));
                setNotification({ open: true, message: 'Draft rule updated successfully', severity: 'success' });
                setEditingRuleId(null);
                setIsUpdatingRow(false);
                return;
            }

            // Inline edit via PATCH
            await SettingsService.updateCancellationRule(id, payload, tenantId);

            // Update local state directly so we don't have to wait for grid refetch to see it
            setFormRules(prev => prev.map(r => r.id === id ? { ...r, ...payload } : r));
            
            setNotification({ open: true, message: 'Cancellation fine tier updated successfully', severity: 'success' });
            setEditingRuleId(null);
            fetchCancellationPolicies(); // keep backend logic in sync
        } catch (error) {
            const errMsg = error?.response?.data?.message || error?.message || 'Failed to update cancellation rule.';
            setNotification({ open: true, message: errMsg, severity: 'error' });
        } finally {
            setIsUpdatingRow(false);
        }
    };

    const handleDeleteRuleClick = (rule) => {
        setRuleToDelete(rule);
        setDeleteRuleModalOpen(true);
    };

    const handleConfirmDeleteRule = async () => {
        if (!ruleToDelete) return;
        setIsDeletingRule(true);
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';

            if (ruleToDelete.id && !ruleToDelete.id.toString().startsWith('r')) {
                await SettingsService.deleteCancellationRule(ruleToDelete.id, tenantId);
            }

            setFormRules(prev => prev.filter(r => r.id !== ruleToDelete.id));
            setNotification({ open: true, message: 'Cancellation rule successfully removed from policy', severity: 'success' });
            fetchCancellationPolicies();
            setDeleteRuleModalOpen(false);
            setRuleToDelete(null);
        } catch (error) {
            const errMsg = error?.response?.data?.message || error?.message || 'Failed to delete cancellation rule.';
            setNotification({ open: true, message: errMsg, severity: 'error' });
        } finally {
            setIsDeletingRule(false);
        }
    };

    const hasNewDraftRules = formRules.some(r => r.id.toString().startsWith('r'));
    const hasDetailsChanged = selectedPolicy && (
        formName.trim() !== selectedPolicy.name ||
        formStatus !== selectedPolicy.status ||
        hasNewDraftRules
    );

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Box>
                    <Typography variant="h4" fontWeight="bold">Cancellation Policies</Typography>
                    <Typography variant="body2" color="text.secondary">Create and manage cancellation policies.</Typography>
                </Box>
                <Button variant="contained" startIcon={<AddIcon sx={{ fontSize: 18 }} />} onClick={handleOpenCreateModal}>
                    Add Policy
                </Button>
            </Box>

            {/* Master Grid */}
            {/* Master Grid */}
            <TableContainer component={Paper} sx={{ mt: 3 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Policy Name</TableCell>
                            <TableCell>Applied To</TableCell>
                            <TableCell>Last Updated</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            Array.from(new Array(4)).map((_, i) => (
                                <TableRow key={i}>
                                    {Array.from(new Array(5)).map((_, j) => (
                                        <TableCell key={j}><Skeleton animation="wave" /></TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : policies.length > 0 ? (
                            policies.map((p) => (
                                <TableRow
                                    key={p.id}
                                    hover
                                    selected={selectedPolicy?.id === p.id}
                                    onClick={() => handleSelectPolicy(p)}
                                    sx={{ cursor: 'pointer' }}
                                >
                                    <TableCell><b>{p.name}</b></TableCell>
                                    <TableCell>{Array.isArray(p.appliedTo) ? p.appliedTo.join(', ') : p.appliedTo}</TableCell>
                                    <TableCell>{p.updatedAt ? new Date(p.updatedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—'}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={p.status}
                                            size="small"
                                            sx={{
                                                bgcolor: p.status === 'Active' ? 'rgba(46,125,50,0.08)' : 'rgba(0,0,0,0.06)',
                                                color: p.status === 'Active' ? 'success.main' : 'text.secondary',
                                                fontWeight: 600
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                                        <IconButton size="small" color="primary" onClick={() => handleSelectPolicy(p)}>
                                            {selectedPolicy?.id === p.id ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                                        </IconButton>
                                        <IconButton 
                                            size="small" 
                                            color="error" 
                                            onClick={() => handleDeleteClick(p)}
                                            sx={{ 
                                                border: '1px solid', 
                                                borderColor: 'error.main',
                                                ml: 1,
                                                '&:hover': { bgcolor: 'error.main', color: 'white' }
                                            }}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                    <Typography color="text.secondary">No cancellation policies found.</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={policies.length === 0 ? 0 : totalPolicies}
                    page={page}
                    onPageChange={(e, np) => setPage(np)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[20, 50, 100]}
                />
            </TableContainer>

            {/* Detail Configuration Panel */}
            {selectedPolicy && (
                <Paper sx={{ borderRadius: '12px', border: '1px solid #eaecf0', boxShadow: 'none', bgcolor: '#ffffff', mt: 3 }}>
                    <Tabs value={detailTab} onChange={(e, v) => setDetailTab(v)} sx={{ borderBottom: 1, borderColor: '#eaecf0', px: 2 }}>
                        <Tab label="Policy Details" />
                    </Tabs>

                    <Box sx={{ p: 3 }}>
                        <TabPanel value={detailTab} index={0}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                    <TextField 
                                        label="Policy Name" 
                                        fullWidth 
                                        size="small" 
                                        value={formName} 
                                        onChange={(e) => setFormName(e.target.value)} 
                                        disabled={isSavingRules} 
                                        required 
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Status</InputLabel>
                                        <Select 
                                            value={formStatus} 
                                            label="Status" 
                                            onChange={(e) => setFormStatus(e.target.value)} 
                                            disabled={isSavingRules}
                                        >
                                            <MenuItem value="Active">Active</MenuItem>
                                            <MenuItem value="Inactive">Inactive</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>

                            {/* Cancellation Rules Sub-Table */}
                            <Box sx={{ mt: 4 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="h6" fontWeight="bold">Cancellation Rules</Typography>
                                    <Button 
                                        variant="contained" 
                                        size="small" 
                                        startIcon={<AddIcon />} 
                                        onClick={() => handleOpenRuleModal()} 
                                        disabled={isSavingRules || getAvailableTimeOptions().length === 0}
                                    >
                                        Add Rule
                                    </Button>
                                </Box>
                                <TableContainer component={Paper}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Time Before Check-in</TableCell>
                                                <TableCell>Cancellation Fee</TableCell>
                                                <TableCell align="right">Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {formRules.length > 0 ? formRules.map((rule) => (
                                                editingRuleId === rule.id ? (
                                                    <TableRow key={rule.id} hover>
                                                        <TableCell>
                                                            <Select size="small" value={inlineRuleData.timeBeforeCheckIn} onChange={e => setInlineRuleData({...inlineRuleData, timeBeforeCheckIn: e.target.value})} disabled={isUpdatingRow} fullWidth>
                                                                {getAvailableTimeOptions(rule.timeBeforeCheckIn).map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                                                            </Select>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                                <Select size="small" value={inlineRuleData.feeType} onChange={e => setInlineRuleData({...inlineRuleData, feeType: e.target.value})} disabled={isUpdatingRow} sx={{ minWidth: 120 }}>
                                                                    <MenuItem value="Percentage">Percentage</MenuItem>
                                                                    <MenuItem value="Fixed">Fixed</MenuItem>
                                                                </Select>
                                                                <TextField size="small" type="number" value={inlineRuleData.feeAmount} onChange={e => setInlineRuleData({...inlineRuleData, feeAmount: e.target.value})} disabled={isUpdatingRow} sx={{ width: 80 }} />
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <IconButton size="small" color="success" onClick={() => handleSaveInlineRule(rule.id)} disabled={isUpdatingRow}>
                                                                {isUpdatingRow ? <CircularProgress size={16} color="inherit" /> : <SaveIcon fontSize="small" />}
                                                            </IconButton>
                                                            <IconButton size="small" color="inherit" onClick={() => setEditingRuleId(null)} disabled={isUpdatingRow}>
                                                                <CloseIcon fontSize="small" />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                    <TableRow key={rule.id} hover>
                                                        <TableCell>{rule.timeBeforeCheckIn}</TableCell>
                                                        <TableCell>{formatFeeDisplay(rule)}</TableCell>
                                                        <TableCell align="right">
                                                            <IconButton size="small" color="primary" onClick={() => handleStartInlineEdit(rule)} disabled={isSavingRules}>
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                            <IconButton 
                                                                size="small" 
                                                                color="error" 
                                                                onClick={() => handleDeleteRuleClick(rule)} 
                                                                disabled={isSavingRules}
                                                                sx={{ 
                                                                    border: '1px solid', 
                                                                    borderColor: 'error.main',
                                                                    ml: 1,
                                                                    '&:hover': { bgcolor: 'error.main', color: 'white' }
                                                                }}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                )
                                            )) : (
                                                <TableRow>
                                                    <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                                                        <Typography color="text.secondary" variant="body2">No rules configured. Click "+ Add Rule" to begin.</Typography>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>

                            {/* Refund & Description */}
                            <Grid container spacing={3} sx={{ mt: 2 }}>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel id="policy-detail-refund-method-label">Refund Method</InputLabel>
                                        <Select 
                                            labelId="policy-detail-refund-method-label"
                                            value={formRefundMethod} 
                                            label="Refund Method" 
                                            onChange={(e) => setFormRefundMethod(e.target.value)} 
                                            disabled={true}
                                        >
                                            {Array.from(new Set([...refundMethodOptions, formRefundMethod].filter(Boolean))).map(m => (
                                                <MenuItem key={m} value={m}>{m}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField label="Description" fullWidth multiline rows={3} size="small" value={formDescription} onChange={(e) => setFormDescription(e.target.value)} disabled={true} />
                                </Grid>
                            </Grid>

                            {/* Action Bar */}
                            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Button 
                                    variant="outlined" 
                                    color="error" 
                                    onClick={() => handleDeleteClick(selectedPolicy)} 
                                    disabled={isSavingRules}
                                    startIcon={<DeleteIcon />}
                                >
                                    Delete Policy
                                </Button>
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <Button variant="outlined" color="inherit" onClick={() => setSelectedPolicy(null)} disabled={isSavingRules}>Cancel</Button>
                                    <Button 
                                        variant="contained" 
                                        onClick={handleSaveChanges} 
                                        disabled={isSavingRules || !hasDetailsChanged}
                                        startIcon={isSavingRules ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                    >
                                        {isSavingRules ? 'Saving Changes...' : 'Save Changes'}
                                    </Button>
                                </Box>
                            </Box>
                        </TabPanel>
                    </Box>
                </Paper>
            )}

            <CreateCancellationPolicyModal
                open={createModalOpen}
                onClose={() => setCreateModalOpen(false)}
                onSubmit={handleCreatePolicy}
                isCreating={isCreating}
                refundMethodOptions={refundMethodOptions}
                loadingRefundMethods={loadingRefundMethods}
                appliedToOptions={APPLIED_TO_OPTIONS}
            />

            {/* Rule Add/Edit Modal */}
            <CancellationRuleModal
                open={ruleModalOpen}
                onClose={() => setRuleModalOpen(false)}
                onSubmit={handleSaveRuleSubmit}
                editingRule={editingRule}
                timeOptions={getAvailableTimeOptions(editingRule?.timeBeforeCheckIn)}
                feeOptions={FEE_OPTIONS}
                formatFeeDisplay={formatFeeDisplay}
            />

            {/* Delete Rule Confirmation */}
            <DeleteConfirmationModal
                open={deleteRuleModalOpen}
                onClose={() => setDeleteRuleModalOpen(false)}
                onConfirm={handleConfirmDeleteRule}
                title="Delete Cancellation Rule"
                message="Are you sure you want to permanently delete this interval tier penalty calculation rule from the cancellation policy structure? This cannot be undone."
                loading={isDeletingRule}
            />

            {/* Delete Policy Confirmation */}
            <DeleteConfirmationModal
                open={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={handleConfirmDelete}
                title="Delete Cancellation Policy"
                message={policyToDelete ? `Are you sure you want to permanently delete "${policyToDelete.name}"? All associated rules will be removed.` : ''}
                loading={isDeleting}
            />

            <Snackbar open={notification.open} autoHideDuration={6000} onClose={() => setNotification({ ...notification, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert severity={notification.severity} sx={{ width: '100%', borderRadius: 2 }}>{notification.message}</Alert>
            </Snackbar>
        </Box>
    );
}
